import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { connect } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2, Loader2 } from 'lucide-react';
import styles from './ProductoForm.module.scss';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';
import NuevoProductoModal from '../../components/Perfil/PerfilMain/MisProductos/NuevoProducto/NuevoProductoModal.tsx';
import {
  openMainModal,
  closeMainModal,
  setPortfolio,
  updatePortfolio,
  deletePortfolio,
  getPortfolio,
  refetchPortfolios,
} from '../../store/actions';
import { buildNavigationParams } from '../../utils/navigationHelpers.ts';

interface PortfolioFormValues {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  zone: string;
  type: string;
  priceUnit: string;
  price: string;
  currencyType: string;
}

interface ProductoFormProps {
  openMainModal: (config: any) => void;
  closeMainModal: () => void;
  setPortfolio: (values: PortfolioFormValues, images: File[], context: string) => void;
  updatePortfolio: (values: PortfolioFormValues, images: File[], id: number, imagesToDelete?: number[]) => void;
  deletePortfolio: (id: number, navigate?: any) => void;
  getPortfolio: (userId?: string | number) => void;
  refetchPortfolios: () => void;
  portfolios: any[];
  userData: any;
  edifySpinner: boolean;
}

const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  }
};

const ProductoForm: React.FC<ProductoFormProps> = ({
  openMainModal,
  closeMainModal,
  setPortfolio,
  updatePortfolio,
  deletePortfolio,
  getPortfolio,
  refetchPortfolios,
  portfolios,
  userData,
  edifySpinner,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<Array<{ id: number; image: string }>>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [formData, setFormData] = useState<PortfolioFormValues>({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    zone: '',
    type: '',
    priceUnit: '',
    price: '',
    currencyType: '',
  });

  const portfolioToEdit = useMemo(() => {
    if (!isEditing || !portfolios || portfolios.length === 0) return null;
    return portfolios.find((p: any) => String(p.id) === String(id));
  }, [isEditing, id, portfolios]);

  useEffect(() => {
    if (isEditing && portfolioToEdit) {
      setFormData({
        name: portfolioToEdit.name || '',
        startDate: formatDateForInput(portfolioToEdit.startDate),
        endDate: formatDateForInput(portfolioToEdit.endDate),
        description: portfolioToEdit.description || '',
        zone: portfolioToEdit.zone || '',
        type: portfolioToEdit.type || '',
        priceUnit: portfolioToEdit.priceUnit || '',
        price: portfolioToEdit.price || '',
        currencyType: portfolioToEdit.currencyType || '',
      });
      if (portfolioToEdit.portfolio_images && portfolioToEdit.portfolio_images.length > 0) {
        setExistingImages(portfolioToEdit.portfolio_images);
      }
    } else if (!isEditing) {
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
        description: '',
        zone: '',
        type: '',
        priceUnit: '',
        price: '',
        currencyType: '',
      });
      setImageFiles([]);
      setExistingImages([]);
      setDeletedImageIds([]);
    }
  }, [isEditing, portfolioToEdit]);

  useEffect(() => {
    if (isEditing && userData) {
      getPortfolio(userData.id);
    }
  }, [isEditing, userData, getPortfolio]);

  const validateDates = (startDate: string, endDate: string): { isValid: boolean; error?: string } => {
    if (!startDate || !endDate) {
      return { isValid: false, error: 'Ambas fechas son requeridas' };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { isValid: false, error: 'Las fechas ingresadas no son válidas' };
    }

    if (start > end) {
      return { isValid: false, error: 'La fecha de inicio debe ser anterior a la fecha de fin' };
    }

    return { isValid: true };
  };

  const validateForm = (data: PortfolioFormValues, images: File[], existing: any[], deleted: number[]): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    const remainingExisting = existing.filter(img => !deleted.includes(img.id));

    if (!data.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    if (!data.description.trim()) {
      errors.description = 'La descripción es requerida';
    }

    if (!data.zone.trim()) {
      errors.zone = 'La zona es requerida';
    }

    if (!data.type) {
      errors.type = 'El tipo es requerido';
    }

    if (!images.length && !remainingExisting.length) {
      errors.images = 'Debe agregar al menos una imagen';
    }

    const dateValidation = validateDates(data.startDate, data.endDate);
    if (!dateValidation.isValid) {
      errors.dates = dateValidation.error || 'Error en las fechas';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const validation = useMemo(() => {
    return validateForm(formData, imageFiles, existingImages, deletedImageIds);
  }, [formData, imageFiles, existingImages, deletedImageIds]);

  const isValid = validation.isValid;
  const formErrors = validation.errors;

  const handleFieldChange = (
    field: keyof PortfolioFormValues,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (newImages: File[]) => {
    setImageFiles(newImages);
  };

  const handleDeleteExistingImage = (imageId: number) => {
    setDeletedImageIds(prev => {
      if (prev.includes(imageId)) return prev;
      return [...prev, imageId];
    });
  };

  const handleCreate = () => {
    if (!isValid) {
      const errorMessages = Object.values(formErrors);
      const mainMessage = errorMessages.length > 0 
        ? errorMessages.join('. ')
        : 'Por favor completa todos los campos requeridos y agrega al menos una imagen.';
      
      openMainModal({
        title: 'Atención',
        body: mainMessage,
        iconType: 'warning',
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    openMainModal({
      title: `¿Estás seguro que deseas crear el ${
        formData.type === 'producto' ? 'producto' : 'servicio'
      } en edify?`,
      iconType: 'warning',
      body: 'Una vez creado será publicado en edify y podrá ser visto por toda la comunidad.',
      isSuccess: false,
      confirmHandler: () => {
        setPortfolio(formData, imageFiles, 'perfil');
        const isFromProfile = location.search.includes('from=perfil');
        if (isFromProfile) {
          const params = new URLSearchParams(location.search);
          const tab = params.get('tab');
          const userId = params.get('userId');
          const userName = params.get('userName');
          if (tab && userId && userName) {
            const navParams = buildNavigationParams(tab, userId, userName);
            navigate(`/perfil/${userId}${navParams}`);
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      },
    });
  };

  const handleUpdate = () => {
    if (!isValid || !id) return;

    openMainModal({
      title: `¿Estás seguro que deseas actualizar el ${
        formData.type === 'producto' ? 'producto' : 'servicio'
      }?`,
      iconType: 'warning',
      body: 'Los cambios se verán reflejados en tu perfil.',
      isSuccess: false,
      confirmHandler: () => {
        updatePortfolio(formData, imageFiles, parseInt(id), deletedImageIds);
        const isFromProfile = location.search.includes('from=perfil');
        if (isFromProfile) {
          const params = new URLSearchParams(location.search);
          const tab = params.get('tab');
          const userId = params.get('userId');
          const userName = params.get('userName');
          if (tab && userId && userName) {
            const navParams = buildNavigationParams(tab, userId, userName);
            navigate(`/perfil/${userId}${navParams}`);
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      },
    });
  };

  const handleDelete = () => {
    if (!id) return;

    openMainModal({
      title: 'Eliminar publicación',
      body: '¿Estás seguro que deseas eliminar esta publicación de tu perfil?',
      iconType: 'warning',
      isSuccess: false,
      confirmHandler: () => {
        deletePortfolio(parseInt(id), navigate);
        const isFromProfile = location.search.includes('from=perfil');
        if (isFromProfile) {
          const params = new URLSearchParams(location.search);
          const tab = params.get('tab');
          const userId = params.get('userId');
          const userName = params.get('userName');
          if (tab && userId && userName) {
            const navParams = buildNavigationParams(tab, userId, userName);
            navigate(`/perfil/${userId}${navParams}`);
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      },
    });
  };

  const handleCancel = () => {
    const isFromProfile = location.search.includes('from=perfil');
    if (isFromProfile) {
      const params = new URLSearchParams(location.search);
      const tab = params.get('tab');
      const userId = params.get('userId');
      const userName = params.get('userName');
      if (tab && userId && userName) {
        const navParams = buildNavigationParams(tab, userId, userName);
        navigate(`/perfil/${userId}${navParams}`);
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}

      <div className={styles.wrapper}>
        <motion.div
          className={styles.productContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className={styles.breadcrumbContainer}>
            <Breadcrumbs />
          </div>

          <main className={styles.main}>
            <section className={styles.section}>
              <div className={styles.header}>
                <button className={styles.backButton} onClick={handleCancel}>
                  <ArrowLeft size={20} />
                </button>
                <h1 className={styles.title}>
                  {isEditing ? 'Editar producto o servicio' : 'Crear nuevo producto o servicio'}
                </h1>
                {isEditing && (
                  <button className={styles.deleteButton} onClick={handleDelete}>
                    <Trash2 size={20} />
                  </button>
                )}
              </div>

              <NuevoProductoModal
                formData={formData}
                onFieldChange={handleFieldChange}
                onImageChange={handleImageChange}
                imageFiles={imageFiles}
                existingImages={existingImages.filter(img => !deletedImageIds.includes(img.id))}
                onDeleteExistingImage={handleDeleteExistingImage}
                isEditing={isEditing}
                dateError={formErrors.dates}
              />

              <div className={styles.actions}>
                <button className={styles.cancelButton} onClick={handleCancel} disabled={edifySpinner}>
                  Cancelar
                </button>
                <button
                  className={`${styles.submitButton} ${!isValid ? styles.disabled : ''}`}
                  disabled={!isValid || edifySpinner}
                  onClick={isEditing ? handleUpdate : handleCreate}
                >
                  {edifySpinner ? (
                    <>
                      <Loader2 size={18} className={styles.spinner} />
                      {isEditing ? 'Guardando...' : 'Publicando...'}
                    </>
                  ) : (
                    isEditing ? 'Guardar cambios' : 'Publicar en mi perfil'
                  )}
                </button>
              </div>
            </section>
          </main>
        </motion.div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  portfolios: state.register.portfolios || [],
  userData: state.general.userData,
  edifySpinner: state.general.edifySpinner,
});

export default connect(mapStateToProps, {
  openMainModal,
  closeMainModal,
  setPortfolio,
  updatePortfolio,
  deletePortfolio,
  getPortfolio,
  refetchPortfolios,
})(ProductoForm);
